import { alpha, Theme } from '@mui/material';
import {
  getSidenavAccentColor,
  getSidenavActiveLeafTextColor,
  getSidenavHoverBackground,
  getSidenavIconColor,
  getSidenavTextColor,
  isSidenavLightSurface,
} from '../sidenav-colors';
import { getSidenavChildIndent, SIDENAV_TOKENS } from '../sidenav-tokens';

type CommonNavButtonStyleOptions = {
  /** When true, this is an expandable parent — keep the accent ribbon active style. */
  hasChildren?: boolean;
  /** When true, active icons stay primary so the rail remains readable without labels. */
  collapsed?: boolean;
};

/** Active label typography: near-black + semibold on light; white + medium on dark. */
function getActiveLeafTextStyles(theme: Theme) {
  const activeTextColor = getSidenavActiveLeafTextColor(theme);
  return {
    color: activeTextColor,
    '& .MuiListItemText-primary': {
      fontWeight: isSidenavLightSurface(theme) ? 600 : ('medium' as const),
      color: activeTextColor,
    },
  };
}

/** Shared ListItemButton styles for sidenav navigation links. */
export function getCommonNavButtonStyles(
  theme: Theme,
  { hasChildren = false, collapsed = false }: CommonNavButtonStyleOptions = {},
) {
  const textColor = getSidenavTextColor(theme);
  const hoverBackground = getSidenavHoverBackground(theme);
  const accentColor = getSidenavAccentColor(theme);
  // Light: lighter tint so the brand ribbon/icon carry the signal; dark: stronger fill.
  const activeBackground = alpha(accentColor, isSidenavLightSurface(theme) ? 0.1 : 0.15);
  const lightSurface = isSidenavLightSurface(theme);

  const { activeAccentWidth, activeAccentInsetX, activeAccentInsetY, activeAccentRadius } =
    SIDENAV_TOKENS;

  return {
    color: textColor,
    position: 'relative',
    transition: theme.transitions.create(['color', 'background-color'], {
      duration: theme.transitions.duration.shorter,
    }),
    // Always render the ribbon so opacity can fade in/out on active change.
    '&::before': {
      content: '""',
      position: 'absolute',
      top: activeAccentInsetY,
      bottom: activeAccentInsetY,
      left: activeAccentInsetX,
      width: activeAccentWidth,
      borderRadius: activeAccentRadius,
      backgroundColor: accentColor,
      opacity: 0,
      pointerEvents: 'none',
      transition: theme.transitions.create('opacity', {
        duration: theme.transitions.duration.shorter,
      }),
    },
    '& .MuiListItemText-primary': {
      transition: theme.transitions.create(['color'], {
        duration: theme.transitions.duration.shorter,
      }),
    },
    '&:hover': {
      background: hoverBackground,
      color: textColor,
      '& .MuiListItemIcon-root': {
        color: textColor,
      },
    },
    '&:focus': {
      background: hoverBackground,
      color: textColor,
      '& .MuiListItemIcon-root': {
        color: textColor,
      },
    },
    '&.active': {
      background: `${activeBackground} !important`,
      '&::before': {
        opacity: 1,
      },
      // Light: parents get the same near-black + semibold anchor as leaves.
      // Expandable parents keep default label weight/color; leaves get the stronger active type.
      ...(hasChildren ? { color: textColor } : getActiveLeafTextStyles(theme)),
      // Brand-tint active icons on light (and always when collapsed) so location pops.
      '& .MuiListItemIcon-root': {
        color: collapsed || lightSurface ? accentColor : 'inherit',
      },
    },
  };
}

/**
 * Top-level nav item packing: height, uppercase label, shared rail padding.
 * Composed with getCommonNavButtonStyles in NavItem.
 */
export function getNavItemLayoutStyles(iconPaddingLeft: number) {
  const tokens = SIDENAV_TOKENS;

  return {
    minHeight: tokens.itemHeight,
    alignItems: 'center' as const,
    // flex-start + shared iconPaddingLeft: icon X is identical expanded/collapsed,
    // and stays put while the drawer width animates.
    justifyContent: 'flex-start' as const,
    pl: `${iconPaddingLeft}px`,
    pr: tokens.contentPaddingX,
    '& .MuiListItemText-primary': {
      fontSize: tokens.fontSize,
      fontWeight: 400,
      lineHeight: 1,
      textTransform: 'uppercase' as const,
    },
  };
}

/** ListItemIcon styles for top-level nav items. */
export function getNavItemIconStyles(theme: Theme) {
  const tokens = SIDENAV_TOKENS;

  return {
    minWidth: 0,
    height: tokens.iconSize,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: getSidenavIconColor(theme),
    // Keep gap reserved when collapsed (text is display:none) so mr never toggles.
    mr: tokens.iconGap,
    '.MuiSvgIcon-root': {
      fontSize: `${tokens.iconSize}px`,
      display: 'block',
    },
  };
}

/**
 * ListItemText visibility for top-level nav items.
 * Collapsed: remove from layout — opacity alone still shifts the icon off-center.
 */
export function getNavItemTextStyles(collapsed: boolean) {
  return [
    {
      my: 0,
      display: 'flex',
      alignItems: 'center',
      // Fill remaining row width so parent labels can right-align expand chevrons.
      flex: 1,
      minWidth: 0,
      '& .MuiListItemText-primary': {
        width: '100%',
        display: 'block',
      },
    },
    collapsed ? { display: 'none' } : { opacity: 1 },
  ];
}

type ChildNavButtonStyleOptions = {
  /** When true, an icon replaces the active primary dot. */
  hasIcon?: boolean;
  /** Compact padding for collapsed hover popover children. */
  inPopover?: boolean;
};

/** Left offset for the active child indicator (dot or icon) in the expanded sidenav. */
function getChildIndicatorLeft(theme: Theme, hasIcon: boolean) {
  const childIndent = getSidenavChildIndent();
  // Icons are wider than the dot — pull them farther left so they clear the label.
  if (hasIcon) {
    return `calc(${childIndent} - ${SIDENAV_TOKENS.activeChildIconSize}px - ${theme.spacing(0.75)})`;
  }

  return `calc(${childIndent} - ${theme.spacing(1.5)})`;
}

/**
 * Nested child link styles: active state shows a primary dot left of the label
 * (or a provided icon in that same slot) instead of the parent accent ribbon.
 */
export function getChildNavButtonStyles(
  theme: Theme,
  { hasIcon = false, inPopover = false }: ChildNavButtonStyleOptions = {},
) {
  const textColor = getSidenavTextColor(theme);
  const hoverBackground = getSidenavHoverBackground(theme);
  const accentColor = getSidenavAccentColor(theme);
  const dotSize = SIDENAV_TOKENS.activeChildDotSize;
  const iconSize = SIDENAV_TOKENS.activeChildIconSize;
  // Matches PopoverTitle horizontal padding.
  const popoverPaddingX = theme.spacing(2.5);
  const indicatorGap = theme.spacing(1);
  const indicatorLeft = inPopover ? popoverPaddingX : getChildIndicatorLeft(theme, hasIcon);
  // Station icons are always visible — reserve gutter so the label never sits under them.
  const popoverIconGutter = `calc(${popoverPaddingX} + ${iconSize}px + ${indicatorGap})`;

  return {
    color: textColor,
    position: 'relative',
    ...(inPopover && {
      pl: hasIcon ? popoverIconGutter : popoverPaddingX,
    }),
    transition: theme.transitions.create(['color', 'background-color', 'padding-left'], {
      duration: theme.transitions.duration.shorter,
    }),
    // Dot indicator always present when no icon — fades via opacity on active.
    ...(!hasIcon && {
      '&::before': {
        content: '""',
        position: 'absolute',
        top: '50%',
        left: indicatorLeft,
        width: dotSize,
        height: dotSize,
        marginTop: `-${dotSize / 2}px`,
        borderRadius: '50%',
        backgroundColor: accentColor,
        opacity: 0,
        pointerEvents: 'none',
        transition: theme.transitions.create('opacity', {
          duration: theme.transitions.duration.shorter,
        }),
      },
    }),
    '& .MuiListItemText-primary': {
      transition: theme.transitions.create(['color'], {
        duration: theme.transitions.duration.shorter,
      }),
    },
    '& .nav-child-indicator': {
      color: textColor,
      transition: theme.transitions.create(['color'], {
        duration: theme.transitions.duration.shorter,
      }),
    },
    '&:hover': {
      background: hoverBackground,
      color: textColor,
    },
    '&:focus': {
      background: hoverBackground,
      color: textColor,
    },
    '&.active': {
      background: 'transparent !important',
      boxShadow: 'none',
      ...getActiveLeafTextStyles(theme),
      // Icon-backed items (e.g. station): brand icon instead of the active dot.
      ...(hasIcon
        ? {
            '& .nav-child-indicator': {
              color: accentColor,
            },
          }
        : {
            // In the popover there is no icon gutter — grow padding so the label clears the dot.
            ...(inPopover && {
              pl: `calc(${popoverPaddingX} + ${dotSize}px + ${indicatorGap})`,
            }),
            '&::before': {
              opacity: 1,
            },
          }),
    },
  };
}

/**
 * Child link packing (height, indent, font) composed with getChildNavButtonStyles.
 */
export function getNavItemChildLayoutStyles(inPopover: boolean) {
  const tokens = SIDENAV_TOKENS;

  return {
    minHeight: tokens.childHeight,
    py: 0.25,
    ...(!inPopover && { pl: getSidenavChildIndent() }),
    pr: 0.5,
    '& .MuiListItemText-primary': {
      fontSize: tokens.fontSize,
      fontWeight: 400,
    },
  };
}

/**
 * Absolute-positioned icon that replaces the active primary dot on child items
 * (e.g. station glyphs).
 */
export function getNavChildIndicatorStyles(theme: Theme, inPopover: boolean) {
  const tokens = SIDENAV_TOKENS;

  return {
    position: 'absolute' as const,
    top: '50%',
    left: inPopover ? theme.spacing(2.5) : getChildIndicatorLeft(theme, true),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transform: 'translateY(-50%)',
    fontSize: tokens.activeChildIconSize,
    lineHeight: 1,
    pointerEvents: 'none' as const,
    '& .MuiSvgIcon-root': {
      fontSize: 'inherit',
    },
  };
}
